import { createEmoteMenu } from '../ui/emoteMenu';
import {
  EMOTES,
  activeGesture,
  emoteAvailable,
  gestureInput,
  type Gesture,
  type EmoteName,
} from '../multiplayer/emotes';
import { randomId } from '../multiplayer/randomId';
import type { Runtime } from './runtime';
import type { InputFrame } from '../controls/types';

export function createEmoteRuntime(runtime: () => Runtime) {
  let solo: Gesture | null = null;
  let pending = false;
  const current = (): Gesture | null => {
    const rt = runtime();
    return rt.network ? rt.network.currentGesture() : activeGesture(solo, Date.now());
  };
  const available = (): boolean => {
    const rt = runtime();
    return (
      !rt.menuOpen &&
      !rt.ui.helpOpen &&
      rt.ui.overlay !== 'visible' &&
      !rt.options.still &&
      !rt.tours.state() &&
      rt.control.controlState.mode === 'walk' &&
      !rt.communications.isOpen
    );
  };
  const perform = async (name: EmoteName): Promise<void> => {
    const rt = runtime();
    if (
      rt.control.controlState.mode !== 'walk' ||
      rt.tours.state() ||
      !emoteAvailable(name, rt.character)
    )
      throw new Error('This gesture is unavailable for your citizen.');
    if (pending || current()) throw new Error('Let the gesture finish.');
    rt.communications.stopFollowing();
    rt.control.controlState = { ...rt.control.controlState, velocity: [0, 0, 0] };
    pending = true;
    try {
      if (rt.gameMode === 'multi') {
        if (!rt.network) throw new Error('The shared city is not connected.');
        await rt.network.emote(name);
      } else {
        solo = {
          id: randomId(),
          name,
          started_at_ms: Date.now(),
          duration_ms: EMOTES[name].durationMs,
        };
      }
    } finally {
      pending = false;
    }
  };
  const menu = createEmoteMenu(
    () => ({
      available: available(),
      character: runtime().character,
      gesture: current(),
      pending,
    }),
    (open) => {
      const rt = runtime();
      rt.input?.setEnabled(!open && !rt.menuOpen && !rt.ui.helpOpen && !rt.communications.isOpen);
    },
    perform
  );
  return {
    current,
    perform,
    epochMs: (): number => runtime().network?.serverEpoch() ?? Date.now(),
    get isOpen(): boolean {
      return menu.isOpen;
    },
    input(frame: InputFrame): InputFrame {
      return gestureInput(frame, pending || Boolean(current()));
    },
    update(): void {
      const rt = runtime();
      if (rt.menuOpen || rt.control.controlState.mode !== 'walk' || rt.tours.state()) solo = null;
      menu.update();
    },
    dispose(): void {
      menu.dispose();
    },
  };
}
